import { CacheProvider } from '@emotion/react'
import { StyledEngineProvider } from '@mui/material/styles'
import { Provider as JotaiProvider } from 'jotai'
import { useState } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { SWRConfig } from 'swr'
import ColorModeProvider from 'src/components/Common/ColorModeProvider'
import { SlotProvider } from 'src/components/Common/slot'
import { globalSWRConfig } from 'src/services/swr'
import {
  createRequestContextValue,
  RequestContextProvider,
  useOptionalRequestContext,
} from 'src/utils/request-context'
import { dynamic } from 'src/utils/ssr'

/**
 * SSR-safe provider shell for the root layout.
 *
 * Contains A-class providers that can render on the server.
 * Also hosts SSR-safe portal slot plumbing used by AppFrameShell.
 * B-class providers (Apollo, QueryClient, Jotai, etc.) and runtime
 * components live inside RootRuntimeInner, loaded client-only via
 * the dynamic import below.
 *
 * Extension points for subsequent Tracks (Step 5 预留):
 *
 * - Track 1: Wrap children with per-request Emotion CacheProvider
 *   (created in entry.server.tsx, passed via React context)
 * - Track 1: ColorModeProvider reads initial value from request cookie
 *   instead of defaulting to 'light' on server
 * - Track 1: Move B-class providers (Apollo, QueryClient, Jotai) here
 *   with request-scoped instances, making <Outlet/> SSR-reachable
 * - Track 0B: i18n Provider receives server-resolved locale from
 *   i18nMiddleware via loadContext
 *
 * @see .claude/skills/ssr-root-provider-split/SKILL.md for classification rules
 */
export function RootShell({ children }: { children: React.ReactNode }) {
  const inheritedRequestContext = useOptionalRequestContext()
  const [localRequestContext] = useState(
    () => inheritedRequestContext ?? createRequestContextValue(),
  )
  const requestContext = inheritedRequestContext ?? localRequestContext

  const content = (
    <CacheProvider value={requestContext.emotionCache}>
      {/*
        StyledEngineProvider is intentionally NOT passed `injectFirst`.
        Internally, `injectFirst` makes `@mui/styled-engine` build its
        own emotion cache (`key: "css"`) using a module-level snapshot
        of `<meta name="emotion-insertion-point">` as the
        `insertionPoint`. That snapshot becomes stale whenever React's
        `<head>` reconciliation briefly detaches the meta during a SPA
        navigation (`<Meta>` / `<Links>` / `<HreflangLinks>` swapping
        children between routes); the next styled-component insert
        then runs `container.insertBefore(newStyle, stalePointer)` and
        throws
          `NotFoundError: Failed to execute 'insertBefore' on 'Node':
           The node before which the new node is to be inserted is
           not a child of this node.`
        which the route Error Boundary catches as a blank page
        (notably on `/zh/market`, `/en/market`). We already worked
        around this for our own request-scoped cache by dropping
        `insertionPoint` in `createEmotionCache` (see request-context),
        but the cache that `<StyledEngineProvider injectFirst>` builds
        is a SEPARATE one we don't control — dropping `injectFirst`
        is the only way to stop it from being created. MUI then falls
        back to the outer `<CacheProvider value={emotionCache}>` we
        already supply, whose `prepend: true` config achieves the same
        "MUI styles at the top of `<head>` so Tailwind / CSS modules
        win the cascade" guarantee `injectFirst` was there for, with
        emotion computing the anchor fresh on every insert instead of
        holding a stale meta ref.
      */}
      <StyledEngineProvider>
        <JotaiProvider store={requestContext.jotaiStore}>
          <ColorModeProvider initialMode={requestContext.colorMode}>
            <HelmetProvider>
              <SWRConfig value={globalSWRConfig}>
                <SlotProvider>
                  {/* TODO(Track 1): move remaining request-scoped B-class providers here */}
                  {children}
                </SlotProvider>
              </SWRConfig>
            </HelmetProvider>
          </ColorModeProvider>
        </JotaiProvider>
      </StyledEngineProvider>
    </CacheProvider>
  )

  return inheritedRequestContext ? (
    content
  ) : (
    <RequestContextProvider value={requestContext}>
      {content}
    </RequestContextProvider>
  )
}

/**
 * Client-only runtime wrapper that provides B-class providers
 * (Apollo, QueryClient, UserPreference, Intercom…) and
 * hosts runtime listener/tracker components for any route subtree that still
 * depends on browser-only providers.
 *
 * Loaded via dynamic({ ssr: false }) so the entire provider subtree
 * and its browser-dependent imports stay off the server bundle.
 *
 * This is a transitional layer — Track 1 will replace it with
 * request-scoped SSR-safe providers inside RootShell directly.
 */
export const RootRuntime = dynamic<{ children?: React.ReactNode }>(
  () => import('./RootRuntimeInner'),
  {
    ssr: false,
  },
)
